/**
 * When Chrome's browser translations are used then that sometimes modifies the DOM
 * in a way that makes the app crash hard. When such browser translating is detected
 * we modify the main React DOM modification methods (insertBefore, insertAfter, removeChild)
 * to silently ignore the errors and carry on.
 * Alternative would be that the app crashes... and then it crashes hard!
 *
 * As long as the isTranslated function is accurate the script should be safe to use.
 *
 * Don't want to modify the scripts by default for performance and out of caution.
 */
const domTranslationGuard = () => {
  const logPrefix = '[DOM Guard]';

  const state = {
    active: false,
    warned: Object.create(null),

    originalRemoveChild: null,
    originalInsertBefore: null,
    originalInsertAfter: null,

    observer: null,
    rafId: 0,
    lastTranslated: false,
  };

  function warnOnce(key, message) {
    if (state.warned[key]) return;
    state.warned[key] = true;
    console.warn(message);
  }

  // Only detects Google Browser translations as it's the only one known to sometimes cause troubles
  function isTranslated() {
    const html = document.documentElement;
    if (!html) return false;

    const cls = html.className || '';
    if (cls.indexOf('translated-') !== -1) return true;
    if (typeof window.google !== 'undefined' && /\btranslated\b/.test(cls)) return true;

    return false;
  }

  function patchRemoveChild() {
    state.originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function removeChildPatched(child) {
      if (child && child.parentNode !== this) {
        warnOnce('removeChild', `${logPrefix} Suppressed removeChild invariant violation`);
        return child;
      }

      return state.originalRemoveChild.call(this, child);
    };
  }

  function patchInsertBefore() {
    state.originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function insertBeforePatched(newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode !== this) {
        warnOnce('insertBefore', `${logPrefix} Suppressed insertBefore invariant violation`);
        return newNode;
      }

      return state.originalInsertBefore.call(this, newNode, referenceNode);
    };
  }

  function patchInsertAfter() {
    const maybeInsertAfter = Node.prototype.insertAfter;
    if (typeof maybeInsertAfter !== 'function') return;

    state.originalInsertAfter = maybeInsertAfter;
    Node.prototype.insertAfter = function insertAfterPatched(newChild, refChild) {
      if (refChild && refChild.parentNode !== this) {
        warnOnce('insertAfter', `${logPrefix} Suppressed insertAfter invariant violation`);
        return newChild;
      }

      return state.originalInsertAfter.call(this, newChild, refChild);
    };
  }

  function applyPatches() {
    if (state.active) return;
    if (typeof Node === 'undefined' || !Node.prototype) return;

    console.warn(`${logPrefix} Browser translation detected. DOM guard patches enabled.`);

    patchRemoveChild();
    patchInsertBefore();
    patchInsertAfter();

    state.active = true;
  }

  function removePatches() {
    if (!state.active) return;
    if (typeof Node === 'undefined' || !Node.prototype) return;

    console.warn(`${logPrefix} Browser translation no longer detected. DOM guard patches disabled.`);

    if (state.originalRemoveChild) Node.prototype.removeChild = state.originalRemoveChild;
    if (state.originalInsertBefore) Node.prototype.insertBefore = state.originalInsertBefore;
    if (state.originalInsertAfter) Node.prototype.insertAfter = state.originalInsertAfter;

    state.originalRemoveChild = null;
    state.originalInsertBefore = null;
    state.originalInsertAfter = null;

    state.active = false;
  }

  function scheduleSync() {
    if (state.rafId) return;
    state.rafId = window.requestAnimationFrame(runSync);
  }

  function runSync() {
    state.rafId = 0;

    const translatedNow = isTranslated();
    if (translatedNow === state.lastTranslated) return;

    state.lastTranslated = translatedNow;
    if (translatedNow) applyPatches();
    else removePatches();
  }

  function onMutations() {
    scheduleSync();
  }

  function start() {
    if (typeof window === 'undefined') return;
    if (typeof MutationObserver === 'undefined') return;

    const html = document.documentElement;
    if (!html) return;

    state.lastTranslated = isTranslated();
    if (state.lastTranslated) applyPatches();

    state.observer = new MutationObserver(onMutations);
    state.observer.observe(html, {
      attributes: true,
      attributeFilter: ['class', 'lang'],
    });
  }

  start();
};

export default domTranslationGuard;
